import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import { useIngestionSSE } from '../../hooks/useIngestionSSE';
import {
  Play,
  Pause,
  Loader2,
  Database,
  Clock,
  ChevronLeft,
  ChevronRight,
  Rows3,
  ScrollText,
  Globe,
  RefreshCw,
  Calendar,
  Activity,
  Trash2,
  Shield,
  TrendingUp,
  Timer,
  ArrowDownToLine,
  X,
  ShieldCheck,
  LayoutGrid,
  List,
  Unplug,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  Layers,
} from 'lucide-react';
import type { ConfigDetail, StoreListItem, MonthCell, DayCell, QueueStatusData, IngestionRun } from './types';
import { STATUS_CONFIG, RUN_TYPE_LABELS, TRIGGER_SOURCE_LABELS, formatDuration, formatMonthLabel, formatRows, cronToHuman } from './types';
import { QuickActionButton, StatCard } from './components';
import { BackfillCalendar } from './BackfillCalendar';
import { InfoBadge } from '../../components/ui/Tooltip';
import { QueuesTab } from './QueuesTab';
import { AmazonTab } from './AmazonTab';
import { BackfillGroupsTab } from './BackfillGroupsTab';

import shopifyLogo from '../../assets/platforms/shopify.svg';
import metaLogo from '../../assets/platforms/meta.svg';
import googleAdsLogo from '../../assets/platforms/google-ads.svg';
import tiktokLogo from '../../assets/platforms/tiktok.svg';
import tiktokShopLogo from '../../assets/platforms/tiktok-shop.svg';
import pinterestLogo from '../../assets/platforms/pinterest.svg';
import klaviyoLogo from '../../assets/platforms/klaviyo.svg';
import amazonAdsLogo from '../../assets/platforms/amazon-ads.svg';
import amazonSellerLogo from '../../assets/platforms/amazon-seller.svg';
import ga4Logo from '../../assets/platforms/ga4.svg';

// ─── Platform metadata ──────────────────────────────

const PLATFORM_META: Record<string, { label: string; icon: string }> = {
  shopify: { label: 'Shopify', icon: shopifyLogo },
  meta: { label: 'Meta Ads', icon: metaLogo },
  google_ads: { label: 'Google Ads', icon: googleAdsLogo },
  tiktok_business: { label: 'TikTok Business', icon: tiktokLogo },
  tiktok_shop: { label: 'TikTok Shop', icon: tiktokShopLogo },
  pinterest: { label: 'Pinterest', icon: pinterestLogo },
  klaviyo: { label: 'Klaviyo', icon: klaviyoLogo },
  amazon_ads: { label: 'Amazon Ads', icon: amazonAdsLogo },
  amazon_seller: { label: 'Amazon Seller', icon: amazonSellerLogo },
  ga4: { label: 'Google Analytics', icon: ga4Logo },
};

// ─── Tab definitions ──────────────────────────────

type TabId = 'overview' | 'calendar' | 'runs' | 'backfills' | 'queues' | 'amazon';

const BASE_TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
  { id: 'runs', label: 'Runs', icon: List },
  { id: 'backfills', label: 'Backfills', icon: Layers },
  { id: 'queues', label: 'Queues', icon: Activity },
];

const RUN_TABS = [
  { value: 'all', label: 'All' },
  { value: 'store-active', label: 'Store Active' },
  { value: 'schedule', label: 'Scheduled' },
  { value: 'admin', label: 'Manual' },
  { value: 'event', label: 'Event' },
] as const;

// ─── Helpers ────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function nextCronRun(cron: string, timezone: string): string | null {
  try {
    // Simple heuristic: parse "30 23 * * *" pattern
    const parts = cron.split(' ');
    if (parts.length < 5) return null;
    const [minute, hour] = parts as [string, string, ...string[]];
    const h = parseInt(hour, 10);
    const m = parseInt(minute, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;

    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
    const nowParts = formatter.formatToParts(now);
    const getP = (type: string) => nowParts.find((p) => p.type === type)?.value ?? '';
    const localHour = parseInt(getP('hour'), 10);
    const localMinute = parseInt(getP('minute'), 10);

    const isToday = localHour < h || (localHour === h && localMinute < m);
    const nextDate = isToday ? 'today' : 'tomorrow';
    return `${nextDate} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} ${timezone}`;
  } catch {
    return null;
  }
}

// ─── Toast ──────────────────────────────────────────

function useToast() {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  return { toast, showToast: setToast };
}

function Toast({ message, type, onDismiss }: { message: string; type: 'success' | 'error'; onDismiss: () => void }) {
  return (
    <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium transition-all ${
      type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
    }`}>
      {type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      {message}
      <button onClick={onDismiss} className="ml-2 opacity-70 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────

export function IngestionConfigDetail() {
  const { storeId: storeIdParam, configId } = useParams<{ storeId: string; configId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const storeId = Number(storeIdParam);

  // Tab persistence in URL
  const tabFromUrl = (searchParams.get('tab') as TabId) || 'overview';
  const [tab, setTabState] = useState<TabId>(tabFromUrl);
  const setTab = useCallback((t: TabId) => {
    setTabState(t);
    setSearchParams({ tab: t }, { replace: true });
  }, [setSearchParams]);

  const [detail, setDetail] = useState<ConfigDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [monthGrid, setMonthGrid] = useState<MonthCell[]>([]);
  const [currentMonthDays, setCurrentMonthDays] = useState<DayCell[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<Set<string>>(new Set());
  const [acting, setActing] = useState<string | null>(null);
  const [queueStatus, setQueueStatus] = useState<QueueStatusData | null>(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [dailyQueueStatus, setDailyQueueStatus] = useState<QueueStatusData | null>(null);
  const [dailyQueueLoading, setDailyQueueLoading] = useState(false);
  const [forceBackfill, setForceBackfill] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [verifyResult, setVerifyResult] = useState<Record<string, unknown> | null>(null);
  const [verifying, setVerifying] = useState(false);
  const { toast, showToast } = useToast();

  const { data: stores } = useQuery({
    queryKey: ['admin', 'stores'],
    queryFn: async ({ signal }) => {
      const res = await api.get<StoreListItem[]>('/api/v1/admin/stores', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load stores');
      return res.data;
    },
  });

  const store = (stores ?? []).find((s) => s.id === storeId);
  const isAmazon = detail?.platform === 'amazon_seller' || detail?.platform === 'amazon_ads';
  const visibleTabs = useMemo(() => {
    if (isAmazon) return [...BASE_TABS, { id: 'amazon' as const, label: 'Amazon', icon: Globe }];
    return BASE_TABS;
  }, [isAmazon]);

  // ─── Data fetchers ────────────────────────────

  const fetchDetail = useCallback(async () => {
    const res = await api.get(`/api/v1/ingestion/admin/configs/${configId}`);
    if (res.success && res.data) setDetail(res.data as ConfigDetail);
    setLoading(false);
  }, [configId]);

  const fetchMonths = useCallback(async () => {
    const res = await api.get(`/api/v1/ingestion/admin/configs/${configId}/months`);
    if (res.success && res.data) {
      const payload = res.data as { grid: MonthCell[]; currentMonthDays?: DayCell[] };
      setMonthGrid(payload.grid);
      if (payload.currentMonthDays) setCurrentMonthDays(payload.currentMonthDays);
    }
  }, [configId]);

  const fetchQueueStatus = useCallback(async () => {
    setQueueLoading(true);
    try {
      const qs = detail ? `?storeId=${detail.storeId}&platform=${encodeURIComponent(detail.platform)}` : '';
      const res = await api.get(`/api/v1/ingestion/admin/queue-status/backfill${qs}`);
      if (res.success && res.data) setQueueStatus(res.data as QueueStatusData);
    } finally {
      setQueueLoading(false);
    }
  }, [detail]);

  const fetchDailyQueueStatus = useCallback(async () => {
    setDailyQueueLoading(true);
    try {
      const qs = detail ? `?storeId=${detail.storeId}&platform=${encodeURIComponent(detail.platform)}` : '';
      const res = await api.get(`/api/v1/ingestion/admin/queue-status/daily-pull${qs}`);
      if (res.success && res.data) setDailyQueueStatus(res.data as QueueStatusData);
    } finally {
      setDailyQueueLoading(false);
    }
  }, [detail]);

  useEffect(() => {
    fetchDetail();
    fetchMonths();
    // Slow fallback poll (60s) — SSE handles real-time updates
    const interval = setInterval(() => { fetchDetail(); fetchMonths(); }, 60_000);
    return () => clearInterval(interval);
  }, [fetchDetail, fetchMonths]);

  // SSE real-time updates — refetch on relevant events + toast notifications
  useIngestionSSE({
    storeId: detail?.storeId,
    platform: detail?.platform,
    enabled: !!detail,
    onEvent: useCallback((type, payload) => {
      if (type === 'day:status' || type === 'config:status' || type === 'run:status' || type === 'backfill:progress') {
        fetchDetail();
        fetchMonths();
      }
      if (type === 'queue:update') {
        fetchQueueStatus();
        fetchDailyQueueStatus();
      }
      // Toast notifications for run completions / errors
      if (type === 'run:status') {
        if (payload.status === 'completed') {
          showToast({ message: `Run completed — ${payload.rowsIngested ?? 0} rows`, type: 'success' });
        } else if (payload.status === 'errored') {
          showToast({ message: `Run failed: ${(payload.errorMessage as string)?.slice(0, 80) ?? 'Unknown error'}`, type: 'error' });
        }
      }
    }, [fetchDetail, fetchMonths, fetchQueueStatus, fetchDailyQueueStatus, showToast]),
  });

  useEffect(() => {
    if (tab === 'queues') {
      fetchQueueStatus();
      fetchDailyQueueStatus();
    }
  }, [tab, fetchQueueStatus, fetchDailyQueueStatus]);

  // ─── Action handlers ──────────────────────────

  async function handleAction(action: string, body?: Record<string, unknown>) {
    setActing(action);
    try {
      if (action === 'pull-now') {
        await api.post('/api/v1/ingestion/admin/pull-now', { storeId: detail!.storeId, platform: detail!.platform });
        showToast({ message: 'Pull triggered', type: 'success' });
      } else if (action === 'pause') {
        await api.post(`/api/v1/ingestion/admin/pause/${configId}`, {});
        showToast({ message: 'Config paused', type: 'success' });
      } else if (action === 'resume') {
        await api.post(`/api/v1/ingestion/admin/resume/${configId}`, {});
        showToast({ message: 'Config resumed', type: 'success' });
      } else if (action === 'backfill-selected') {
        await api.post('/api/v1/ingestion/admin/backfill', {
          storeId: detail!.storeId, platform: detail!.platform,
          months: [...selectedMonths].sort(), ...(forceBackfill ? { force: true } : {}),
        });
        showToast({ message: `Backfill started for ${selectedMonths.size} month${selectedMonths.size !== 1 ? 's' : ''}`, type: 'success' });
        setSelectedMonths(new Set());
      } else if (action === 'backfill-preset' && body) {
        await api.post('/api/v1/ingestion/admin/backfill', {
          storeId: detail!.storeId, platform: detail!.platform, ...(forceBackfill ? { force: true } : {}), ...body,
        });
        showToast({ message: 'Backfill started', type: 'success' });
      }
      await fetchDetail();
      await fetchMonths();
    } catch (err) {
      showToast({ message: err instanceof Error ? err.message : 'Action failed', type: 'error' });
    } finally {
      setActing(null);
    }
  }

  async function handleRetryJob(jobId: string, queue = 'backfill') {
    setActing(`retry-${jobId}`);
    try {
      await api.post('/api/v1/ingestion/admin/retry-job', { queueName: queue, jobId });
      showToast({ message: 'Job retried', type: 'success' });
      if (queue === 'backfill') await fetchQueueStatus();
      else await fetchDailyQueueStatus();
    } finally { setActing(null); }
  }

  async function handleRemoveJob(jobId: string, queueName = 'backfill') {
    setActing(`remove-${jobId}`);
    try {
      await api.post('/api/v1/ingestion/admin/remove-job', { queueName, jobId });
      showToast({ message: 'Job removed', type: 'success' });
      if (queueName === 'backfill') await fetchQueueStatus();
      else await fetchDailyQueueStatus();
    } finally { setActing(null); }
  }

  async function handleClearData() {
    if (!detail) return;
    setClearing(true);
    try {
      await api.post('/api/v1/ingestion/admin/clear-data', { storeId: detail.storeId, platform: detail.platform });
      setClearConfirm(false);
      showToast({ message: 'Data cleared', type: 'success' });
      await fetchDetail();
      await fetchMonths();
    } finally { setClearing(false); }
  }

  async function handleGmvMaxBackfill(days: number) {
    if (!detail) return;
    setActing('gmv-max');
    try {
      const endDate = new Date().toISOString().slice(0, 10);
      const startDate = new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
      await api.post(
        '/api/v1/ingestion/admin/gmv-max-backfill',
        { storeId: detail.storeId, startDate, endDate },
      );
      showToast({ message: `GMV Max backfill complete (${days}d)`, type: 'success' });
    } catch (err) {
      showToast({ message: err instanceof Error ? err.message : 'GMV Max backfill failed', type: 'error' });
    } finally {
      setActing(null);
    }
  }

  async function handleDrainQueue(queueName = 'backfill') {
    setActing(`drain-${queueName}`);
    try {
      await api.post('/api/v1/ingestion/admin/drain-queue', { queueName });
      showToast({ message: `Queue ${queueName} drained`, type: 'success' });
      if (queueName === 'backfill') await fetchQueueStatus();
      else await fetchDailyQueueStatus();
    } finally { setActing(null); }
  }

  async function handleRetryAllFailed(queueName: string) {
    setActing(`retry-all-${queueName}`);
    try {
      await api.post('/api/v1/ingestion/admin/retry-all-failed', {
        queueName, storeId: detail!.storeId, platform: detail!.platform,
      });
      showToast({ message: 'All failed jobs retried', type: 'success' });
      if (queueName === 'backfill') await fetchQueueStatus();
      else await fetchDailyQueueStatus();
    } finally { setActing(null); }
  }

  async function handleRemoveAllFailed(queueName: string) {
    setActing(`remove-all-${queueName}`);
    try {
      await api.post('/api/v1/ingestion/admin/remove-all-failed', {
        queueName, storeId: detail!.storeId, platform: detail!.platform,
      });
      showToast({ message: 'All failed jobs removed', type: 'success' });
      if (queueName === 'backfill') await fetchQueueStatus();
      else await fetchDailyQueueStatus();
    } finally { setActing(null); }
  }

  async function handleRetryRun(run: IngestionRun) {
    setActing(`retry-run-${run.id}`);
    try {
      await api.post('/api/v1/ingestion/admin/pull-now', {
        storeId: run.storeId, platform: run.platform,
      });
      showToast({ message: 'Retry triggered', type: 'success' });
      await fetchDetail();
    } finally { setActing(null); }
  }

  async function handleVerify() {
    if (!detail) return;
    setVerifying(true);
    try {
      const endDate = new Date().toISOString().slice(0, 10);
      const start = new Date();
      start.setDate(start.getDate() - 90);
      const startDate = start.toISOString().slice(0, 10);
      const res = await api.post('/api/v1/ingestion/admin/verify', {
        storeId: detail.storeId, platform: detail.platform, startDate, endDate,
      });
      if (res.success && res.data) setVerifyResult(res.data as Record<string, unknown>);
    } finally { setVerifying(false); }
  }

  function toggleMonth(month: string) {
    setSelectedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(month)) next.delete(month); else next.add(month);
      return next;
    });
  }

  function selectPresetMonths(count: number) {
    setSelectedMonths(new Set(monthGrid.slice(1, count + 1).map((m) => m.month)));
  }

  function selectAllMonths() {
    setSelectedMonths(new Set(monthGrid.slice(1).map((m) => m.month)));
  }

  function deselectAllMonths() {
    setSelectedMonths(new Set());
  }

  // ─── Derived data ──────────────────────────────

  const runs = detail?.recentRuns ?? [];
  const completedRuns = runs.filter((r) => r.status === 'completed');
  const erroredRuns = runs.filter((r) => r.status === 'errored');
  const totalRows = monthGrid.reduce((sum, m) => sum + (m.rows ?? 0), 0);
  const avgDuration = completedRuns.length > 0
    ? completedRuns
        .filter((r) => r.startedAt && r.completedAt)
        .reduce((sum, r) => sum + (new Date(r.completedAt!).getTime() - new Date(r.startedAt).getTime()), 0) / completedRuns.length
    : 0;
  const successRate = runs.length > 0 ? Math.round((completedRuns.length / runs.length) * 100) : 0;
  const lastError = erroredRuns[0];
  const nextPull = detail ? nextCronRun(detail.scheduleCron, detail.timezone) : null;

  const queueJobCount =
    (queueStatus ? queueStatus.counts.active + queueStatus.counts.failed + queueStatus.counts.waiting : 0) +
    (dailyQueueStatus ? dailyQueueStatus.counts.active + dailyQueueStatus.counts.failed : 0);

  const isConnected = (store?.integrations ?? []).includes(detail?.platform ?? '');

  const pm = PLATFORM_META[detail?.platform ?? ''];
  const platformLabel = pm?.label ?? detail?.platform ?? '';
  const platformIcon = pm?.icon;

  // Coverage calculation from month grid
  const coveredMonths = monthGrid.filter((m) => m.status === 'completed').length;
  const totalMonths = monthGrid.length;
  const coveragePct = totalMonths > 0 ? Math.round((coveredMonths / totalMonths) * 100) : 0;

  // ─── Loading / Error states ───────────────────

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48" />
        <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div>
        <Link to={`/ingestion/${storeId}`} className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4">
          <ChevronLeft className="w-4 h-4" /> Back
        </Link>
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">Config not found</p>
        </div>
      </div>
    );
  }

  const s = STATUS_CONFIG[detail.status] ?? STATUS_CONFIG['active']!;
  const StatusIcon = s.icon;

  return (
    <div>
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => showToast(null)} />}

      {/* ─── Breadcrumb with platform icon ──────── */}
      <Link to={`/ingestion/${storeId}`} className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-3">
        <ChevronLeft className="w-4 h-4" />
        {platformIcon && <img src={platformIcon} alt="" className="w-4 h-4" />}
        {store?.name ?? `Store #${storeId}`}
      </Link>

      {/* ─── Disconnected Warning ─────────────────── */}
      {!isConnected && (
        <div className="flex items-center gap-2.5 px-4 py-2.5 mb-4 rounded-xl border border-dashed border-red-300 dark:border-red-900/50 bg-red-50 dark:bg-red-950/40">
          <Unplug className="w-4 h-4 text-red-400 shrink-0" />
          <div>
            <span className="text-xs font-medium text-red-700 dark:text-red-400">No connection</span>
            <span className="text-xs text-red-600 dark:text-red-400 ml-1.5">-- this platform has no active integration. Pulls and backfills will fail.</span>
          </div>
        </div>
      )}

      {/* ─── Header: Platform Icon + Title + Status + Actions ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          {platformIcon && (
            <div className="w-10 h-10 rounded-lg flex items-center justify-center border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shrink-0">
              <img src={platformIcon} alt={platformLabel} className="w-6 h-6" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{platformLabel}</h2>
              <InfoBadge tip={
                detail.status === 'active' ? 'Config is running normally. Daily pulls fire on schedule and data is being ingested.' :
                detail.status === 'paused' ? 'Config is paused. No scheduled pulls will run. Resume to restart data ingestion.' :
                detail.status === 'backfilling' ? 'Historical data is being backfilled. Daily pulls are paused until backfill completes.' :
                'Config is disabled, usually because the platform connection was lost.'
              }>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${s.className}`}>
                  <StatusIcon className="w-3.5 h-3.5" />
                  {s.label}
                </span>
              </InfoBadge>
              {!isConnected && (
                <InfoBadge tip="This platform's OAuth connection has been removed or expired. Reconnect from the Integrations page to resume data ingestion.">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/50">
                    <Unplug className="w-3 h-3" /> Disconnected
                  </span>
                </InfoBadge>
              )}
            </div>
            {nextPull && detail.status === 'active' && (
              <InfoBadge tip="Estimated next scheduled data pull based on the cron schedule and store timezone. The pull fetches the current day's data.">
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Next pull: {nextPull}</p>
              </InfoBadge>
            )}
          </div>
        </div>
        <div className={`flex flex-wrap items-center gap-1.5 ${!isConnected ? 'opacity-40 pointer-events-none' : ''}`}>
          <QuickActionButton icon={RefreshCw} label="Pull Now" loading={acting === 'pull-now'} onClick={() => handleAction('pull-now')} />
          {(detail.status === 'active' || detail.status === 'backfilling') && (
            <QuickActionButton icon={Pause} label="Pause" loading={acting === 'pause'} onClick={() => handleAction('pause')} variant="warning" />
          )}
          {detail.status === 'paused' && (
            <QuickActionButton icon={Play} label="Resume" loading={acting === 'resume'} onClick={() => handleAction('resume')} variant="success" />
          )}
          <QuickActionButton icon={ArrowDownToLine} label="3 mo" loading={acting === 'backfill-preset'} onClick={() => handleAction('backfill-preset', { days: 90 })} />
          <QuickActionButton icon={ArrowDownToLine} label="6 mo" loading={acting === 'backfill-preset'} onClick={() => handleAction('backfill-preset', { days: 180 })} />
          <QuickActionButton icon={ArrowDownToLine} label="12 mo" loading={acting === 'backfill-preset'} onClick={() => handleAction('backfill-preset', { days: 365 })} />
          {detail.platform === 'tiktok_business' && (
            <>
              <QuickActionButton icon={BarChart3} label="GMV Max 3mo" loading={acting === 'gmv-max'} onClick={() => handleGmvMaxBackfill(90)} />
              <QuickActionButton icon={BarChart3} label="GMV Max 6mo" loading={acting === 'gmv-max'} onClick={() => handleGmvMaxBackfill(180)} />
            </>
          )}
          <QuickActionButton icon={ShieldCheck} label="Verify" loading={verifying} onClick={handleVerify} variant="success" />
        </div>
      </div>

      {/* ─── Quick Stats Bar ──────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <StatCard label="Total Rows" value={totalRows.toLocaleString()} icon={Rows3} />
        <InfoBadge tip="Percentage of runs that completed successfully out of all runs shown. A rate below 80% may indicate persistent issues.">
          <StatCard label="Success Rate" value={`${successRate}%`} icon={TrendingUp} accent={successRate < 80 ? 'red' : undefined} />
        </InfoBadge>
        <StatCard label="Avg Duration" value={avgDuration ? formatDuration(avgDuration) : '--'} icon={Timer} />
        <InfoBadge tip="Percentage of months that have at least some ingested data. 100% means every month in the grid has data.">
          <StatCard label="Coverage" value={`${coveragePct}%`} icon={BarChart3} accent={coveragePct < 80 ? 'red' : undefined} />
        </InfoBadge>
        <StatCard label="Runs" value={`${completedRuns.length} ok / ${erroredRuns.length} err`} icon={Database} accent={erroredRuns.length > 0 ? 'red' : undefined} />
      </div>

      {/* ─── Data Coverage Bar ────────────────────── */}
      {totalMonths > 0 && (
        <InfoBadge tip="Percentage of months that have at least some ingested data. 100% means every month in the grid has data.">
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-2.5 mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-gray-500 dark:text-gray-400">Data Coverage</span>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{coveredMonths}/{totalMonths} months</span>
            </div>
            <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${coveragePct === 100 ? 'bg-green-500' : coveragePct > 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
                style={{ width: `${coveragePct}%` }}
              />
            </div>
          </div>
        </InfoBadge>
      )}

      {/* ─── Last Error Preview ───────────────────── */}
      {lastError && (
        <InfoBadge tip="The most recent ingestion run that failed. Click 'View' to see full error logs and retry.">
          <div className="flex items-start gap-2.5 px-4 py-2.5 mb-4 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/40">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-red-700 dark:text-red-400">Last Error</span>
                <span className="text-[10px] text-red-400">{timeAgo(lastError.startedAt)}</span>
              </div>
              <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 truncate">{lastError.errorMessage}</p>
            </div>
            <button
              onClick={() => navigate(`/ingestion/${storeId}/${configId}/runs/${lastError.id}`)}
              className="text-[10px] text-red-600 dark:text-red-400 hover:underline shrink-0"
            >
              View
            </button>
          </div>
        </InfoBadge>
      )}

      {/* ─── Verify Results (dismissible) ─────────── */}
      {verifyResult && (
        <VerifyBanner result={verifyResult} onDismiss={() => setVerifyResult(null)} />
      )}

      {/* ─── Tab Bar ─────────────────────────────── */}
      <div className="flex gap-0.5 border-b border-gray-200 dark:border-gray-800 mb-4">
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                isActive
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              {t.id === 'runs' && runs.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 ml-0.5">{runs.length}</span>
              )}
              {t.id === 'queues' && queueJobCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 ml-0.5">{queueJobCount}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ─── Tab Content ─────────────────────────── */}
      {tab === 'overview' && (
        <OverviewTab
          detail={detail}
          totalRows={totalRows}
          completedRuns={completedRuns.length}
          erroredRuns={erroredRuns.length}
          avgDuration={avgDuration}
          clearConfirm={clearConfirm}
          clearing={clearing}
          nextPull={nextPull}
          onClearConfirm={() => setClearConfirm(true)}
          onClearCancel={() => setClearConfirm(false)}
          onClearData={handleClearData}
        />
      )}

      {tab === 'calendar' && (
        <CalendarTab
          detail={detail}
          currentMonthDays={currentMonthDays}
          monthGrid={monthGrid}
          selectedMonths={selectedMonths}
          acting={acting}
          configId={configId!}
          forceBackfill={forceBackfill}
          onToggleForce={setForceBackfill}
          onToggleMonth={toggleMonth}
          onSelectPreset={selectPresetMonths}
          onSelectAll={selectAllMonths}
          onDeselectAll={deselectAllMonths}
          onBackfillSelected={() => handleAction('backfill-selected')}
          onRefresh={() => { fetchDetail(); fetchMonths(); }}
        />
      )}

      {tab === 'runs' && (
        <RunsTab
          runs={runs}
          storeId={storeId}
          configId={configId!}
          acting={acting}
          navigate={navigate}
          onRetryRun={handleRetryRun}
        />
      )}

      {tab === 'backfills' && (
        <BackfillGroupsTab storeId={storeId} platform={detail.platform} />
      )}

      {tab === 'queues' && (
        <QueuesTab
          detail={detail}
          queueStatus={queueStatus}
          queueLoading={queueLoading}
          dailyQueueStatus={dailyQueueStatus}
          dailyQueueLoading={dailyQueueLoading}
          acting={acting}
          onRefreshBackfill={fetchQueueStatus}
          onRefreshDaily={fetchDailyQueueStatus}
          onRetryJob={handleRetryJob}
          onRemoveJob={handleRemoveJob}
          onDrainQueue={handleDrainQueue}
          onRetryAllFailed={handleRetryAllFailed}
          onRemoveAllFailed={handleRemoveAllFailed}
        />
      )}

      {tab === 'amazon' && isAmazon && (
        <AmazonTab detail={detail} configId={configId!} />
      )}
    </div>
  );
}

// ─── Verify Banner ────────────────────────────────

function VerifyBanner({ result, onDismiss }: { result: Record<string, unknown>; onDismiss: () => void }) {
  const healthy = !!result['healthy'];
  return (
    <div className={`rounded-xl border px-4 py-3 mb-4 ${healthy ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className={`w-3.5 h-3.5 ${healthy ? 'text-green-500' : 'text-amber-500'}`} />
          <span className={`text-xs font-medium ${healthy ? 'text-green-700' : 'text-amber-700'}`}>
            {healthy ? 'Data Healthy' : 'Issues Found'}
          </span>
        </div>
        <button onClick={onDismiss} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
          <X className="w-3 h-3" />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1.5 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Coverage</span>
          <span className="font-medium">{String(result['coverage'] ?? '--')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Days</span>
          <span className="font-medium">{String(result['coveredDays'] ?? 0)} / {String(result['expectedDays'] ?? 0)}</span>
        </div>
        {!!result['clickhouse'] && typeof result['clickhouse'] === 'object' && (
          <>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">CH Rows</span>
              <span className="font-medium">{Number((result['clickhouse'] as Record<string, unknown>)['totalRows'] ?? 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">CH Days</span>
              <span className="font-medium">{String((result['clickhouse'] as Record<string, unknown>)['dayCount'] ?? 0)}</span>
            </div>
          </>
        )}
      </div>
      {Array.isArray(result['missingDays']) && (result['missingDays'] as string[]).length > 0 && (
        <div className="mt-2 pt-2 border-t border-amber-200 dark:border-amber-900/50">
          <span className="text-[10px] text-amber-600 font-medium">Missing: </span>
          <span className="text-[10px] text-amber-700 dark:text-amber-300">
            {(result['missingDays'] as string[]).length <= 10
              ? (result['missingDays'] as string[]).join(', ')
              : `${(result['missingDays'] as string[]).slice(0, 10).join(', ')} +${(result['missingDays'] as string[]).length - 10} more`}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────

function OverviewTab({
  detail,
  totalRows,
  completedRuns,
  erroredRuns,
  avgDuration,
  clearConfirm,
  clearing,
  nextPull,
  onClearConfirm,
  onClearCancel,
  onClearData,
}: {
  detail: ConfigDetail;
  totalRows: number;
  completedRuns: number;
  erroredRuns: number;
  avgDuration: number;
  clearConfirm: boolean;
  clearing: boolean;
  nextPull: string | null;
  onClearConfirm: () => void;
  onClearCancel: () => void;
  onClearData: () => void;
}) {
  return (
    <div className="space-y-4 max-w-2xl">
      {/* Data Freshness */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Data Freshness</span>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
          <FreshnessRow label="Last Pull" value={detail.lastScheduledAt} />
          <FreshnessRow label="Last Backfill" value={detail.lastBackfillAt} />
          <FreshnessRow label="Last Store Active" value={detail.lastStoreActiveAt} />
          {nextPull && (
            <div className="flex justify-between">
              <span className="text-gray-400 dark:text-gray-500">Next Pull</span>
              <span className="text-blue-600 dark:text-blue-400 font-medium">{nextPull}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-400 dark:text-gray-500">Schedule</span>
            <span className="text-gray-700 dark:text-gray-200 font-medium" title={detail.scheduleCron}>{cronToHuman(detail.scheduleCron)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400 dark:text-gray-500">Timezone</span>
            <span className="text-gray-700 dark:text-gray-200 font-medium">{detail.timezone}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400 dark:text-gray-500">Config ID</span>
            <span className="text-gray-500 dark:text-gray-400 font-mono text-[10px]">{detail.id.slice(0, 8)}</span>
          </div>
        </div>
      </div>

      {/* Ad Account Override */}
      <AdAccountOverride storeId={detail.storeId} platform={detail.platform} />

      {/* Shopify Webhooks */}
      {detail.platform === 'shopify' && (
        <ShopifyWebhookSection storeId={detail.storeId} />
      )}

      {/* Danger Zone */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-red-200 dark:border-red-900/50 px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-3.5 h-3.5 text-red-400" />
          <span className="text-xs font-medium text-red-700 dark:text-red-400">Danger Zone</span>
        </div>
        {!clearConfirm ? (
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Delete all data + run history for this config</span>
            <button
              onClick={onClearConfirm}
              className="px-2 py-1 text-[10px] font-medium bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-950/40"
            >
              Clear Data
            </button>
          </div>
        ) : (
          <div>
            <p className="text-xs text-red-700 dark:text-red-400 mb-2">
              Delete <strong>all</strong> analytics data and run history for <strong className="capitalize">{detail.platform}</strong> on store #{detail.storeId}? This cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={onClearData}
                disabled={clearing}
                className="px-2 py-1 text-[10px] font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
              >
                {clearing && <Loader2 className="w-3 h-3 animate-spin" />}
                Delete Everything
              </button>
              <button onClick={onClearCancel} className="px-2 py-1 text-[10px] font-medium border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-950">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FreshnessRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-400 dark:text-gray-500">{label}</span>
      <span className="text-gray-700 dark:text-gray-200 font-medium">
        {value ? (
          <span title={new Date(value).toLocaleString()}>{timeAgo(value)}</span>
        ) : '--'}
      </span>
    </div>
  );
}

// ─── Ad Account Override ─────────────────────────────

const ACCOUNT_PLATFORMS = new Set(['meta', 'google_ads', 'pinterest', 'ga4', 'amazon_ads']);

interface AdAccountItem {
  id: string;
  name: string;
  currency?: string;
  country?: string;
  status?: string | number;
}

interface AccountsResponse {
  currentAccountId: string | null;
  currentCurrency: string | null;
  accounts: AdAccountItem[];
}

function AdAccountOverride({ storeId, platform }: { storeId: number; platform: string }) {
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState<AdAccountItem[]>([]);
  const [currentAccountId, setCurrentAccountId] = useState<string | null>(null);
  const [currentCurrency, setCurrentCurrency] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!ACCOUNT_PLATFORMS.has(platform)) return null;

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<AccountsResponse>(`/api/v1/admin/stores/${storeId}/accounts/${platform}`);
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load accounts');
      setAccounts(res.data.accounts);
      setCurrentAccountId(res.data.currentAccountId);
      setCurrentCurrency(res.data.currentCurrency);
      setSelectedId(res.data.currentAccountId);
      setOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError(null);
    try {
      const account = accounts.find((a) => a.id === selectedId);
      const res = await api.post(`/api/v1/admin/stores/${storeId}/accounts/${platform}/select`, {
        accountId: selectedId,
        accountName: account?.name,
        currency: account?.currency,
      });
      if (!res.success) throw new Error(res.error ?? 'Failed to save');
      setCurrentAccountId(selectedId);
      setCurrentCurrency(account?.currency ?? null);
      setSuccess(`Switched to ${account?.name ?? selectedId}`);
      setOpen(false);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const filtered = search
    ? accounts.filter((a) => {
        const q = search.toLowerCase();
        return a.name?.toLowerCase().includes(q) || a.id.toLowerCase().includes(q) || a.currency?.toLowerCase().includes(q);
      })
    : accounts;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <Globe className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Ad Account</span>
      </div>

      {/* Current account display */}
      <div className="flex items-center justify-between">
        <div className="text-xs">
          {currentAccountId ? (
            <span className="text-gray-700 dark:text-gray-200">
              <span className="font-mono text-[10px] bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">{currentAccountId}</span>
              {currentCurrency && <span className="text-gray-400 dark:text-gray-500 ml-1.5">{currentCurrency}</span>}
            </span>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">No account selected</span>
          )}
        </div>
        <button
          onClick={fetchAccounts}
          disabled={loading}
          className="px-2 py-1 text-[10px] font-medium border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50 flex items-center gap-1"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          {currentAccountId ? 'Change' : 'Select Account'}
        </button>
      </div>

      {/* Success message */}
      {success && (
        <div className="mt-2 text-[10px] text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900/50 rounded-lg px-2 py-1 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> {success}
        </div>
      )}

      {/* Error message */}
      {error && !open && (
        <div className="mt-2 text-[10px] text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg px-2 py-1 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> {error}
        </div>
      )}

      {/* Expanded account picker */}
      {open && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
          {/* Search */}
          {accounts.length > 3 && (
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search accounts..."
              autoFocus
              className="w-full mb-2 px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
            />
          )}

          {/* Account list */}
          <div className="max-h-48 overflow-y-auto space-y-1">
            {filtered.map((account) => {
              const isSelected = selectedId === account.id;
              const isCurrent = account.id === currentAccountId;
              return (
                <button
                  key={account.id}
                  type="button"
                  onClick={() => setSelectedId(account.id)}
                  className={`flex w-full items-center gap-2 px-2.5 py-2 rounded-lg border text-left transition-all text-xs ${
                    isSelected
                      ? 'border-gray-900 bg-gray-50 dark:bg-gray-950 ring-1 ring-gray-900'
                      : 'border-gray-100 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 flex items-center justify-center ${
                    isSelected ? 'border-gray-900' : 'border-gray-300'
                  }`}>
                    {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-gray-900 dark:bg-gray-950" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-gray-800 dark:text-gray-100 truncate">{account.name || account.id}</span>
                      {isCurrent && (
                        <span className="shrink-0 text-[9px] px-1 py-0.5 rounded bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400 font-medium">Current</span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate">
                      {account.id}
                      {account.currency && <> &middot; {account.currency}</>}
                      {account.country && <> &middot; {account.country}</>}
                    </div>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 py-2 text-center">
                {search ? 'No accounts match search' : 'No accounts found'}
              </p>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mt-2 text-[10px] text-red-600 dark:text-red-400">{error}</div>
          )}

          {/* Actions */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => { setOpen(false); setSearch(''); setError(null); }}
              className="px-2.5 py-1 text-[10px] font-medium border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-950"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!selectedId || selectedId === currentAccountId || saving}
              className="px-2.5 py-1 text-[10px] font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              {saving ? 'Saving...' : 'Save Override'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Shopify Webhook Section ─────────────────────────────

interface WebhookItem {
  id: number;
  topic: string;
  address: string;
}

const WEBHOOK_CALLBACK_BASE_URL = import.meta.env.VITE_API_WEBHOOK_BASE_URL as string | undefined ?? '';

function ShopifyWebhookSection({ storeId }: { storeId: number }) {
  const [webhooks, setWebhooks] = useState<WebhookItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  const fetchWebhooks = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ webhooks: WebhookItem[] }>(`/api/v1/admin/stores/${storeId}/webhooks/shopify`);
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load webhooks');
      setWebhooks(res.data.webhooks);
      setFetched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!WEBHOOK_CALLBACK_BASE_URL) {
      setError('VITE_API_WEBHOOK_BASE_URL is not configured');
      return;
    }
    setRegistering(true);
    setError(null);
    try {
      const res = await api.post<{ registered: WebhookItem[] }>(`/api/v1/admin/stores/${storeId}/webhooks/shopify/register`, {
        callbackBaseUrl: WEBHOOK_CALLBACK_BASE_URL,
      });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to register webhooks');
      setSuccess(`Registered ${res.data.registered.length} webhook(s)`);
      setTimeout(() => setSuccess(null), 4000);
      await fetchWebhooks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register webhooks');
    } finally {
      setRegistering(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <Globe className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Webhooks</span>
      </div>

      {!fetched ? (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400 dark:text-gray-500">Real-time order/refund updates</span>
          <button
            onClick={fetchWebhooks}
            disabled={loading}
            className="px-2 py-1 text-[10px] font-medium border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50 flex items-center gap-1"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Load Webhooks
          </button>
        </div>
      ) : (
        <div>
          {webhooks.length > 0 ? (
            <div className="space-y-1.5 mb-2">
              {webhooks.map((wh) => (
                <div key={wh.id} className="flex items-center justify-between text-[10px] bg-gray-50 dark:bg-gray-950 border border-gray-100 dark:border-gray-800 rounded-lg px-2.5 py-1.5">
                  <span className="font-mono text-gray-700 dark:text-gray-200">{wh.topic}</span>
                  <span className="text-gray-400 dark:text-gray-500 truncate ml-2 max-w-[200px]" title={wh.address}>{wh.address}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">No webhooks registered</p>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={handleRegister}
              disabled={registering}
              className="px-2 py-1 text-[10px] font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1"
            >
              {registering && <Loader2 className="w-3 h-3 animate-spin" />}
              Register Webhooks
            </button>
            <button
              onClick={fetchWebhooks}
              disabled={loading}
              className="px-2 py-1 text-[10px] font-medium border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50 flex items-center gap-1"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Refresh
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="mt-2 text-[10px] text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900/50 rounded-lg px-2 py-1 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> {success}
        </div>
      )}

      {error && (
        <div className="mt-2 text-[10px] text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg px-2 py-1 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> {error}
        </div>
      )}
    </div>
  );
}

// ─── Calendar Tab ─────────────────────────────────

function CalendarTab({
  detail,
  currentMonthDays,
  monthGrid,
  selectedMonths,
  acting,
  configId,
  forceBackfill,
  onToggleForce,
  onToggleMonth,
  onSelectPreset,
  onSelectAll,
  onDeselectAll,
  onBackfillSelected,
  onRefresh,
}: {
  detail: ConfigDetail;
  currentMonthDays: DayCell[];
  monthGrid: MonthCell[];
  selectedMonths: Set<string>;
  acting: string | null;
  configId: string;
  forceBackfill: boolean;
  onToggleForce: (v: boolean) => void;
  onToggleMonth: (month: string) => void;
  onSelectPreset: (count: number) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onBackfillSelected: () => void;
  onRefresh: () => void;
}) {
  const totalActiveJobs = monthGrid.reduce((sum, m) => sum + (m.activeJobs ?? 0), 0);
  const runningMonths = monthGrid.filter(m => (m.activeJobs ?? 0) > 0).map(m => formatMonthLabel(m.month));

  return (
    <div className="space-y-4">
      {/* Active jobs banner */}
      {totalActiveJobs > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900/50 rounded-lg animate-pulse">
          <Loader2 className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin" />
          <div className="text-xs">
            <span className="font-medium text-blue-800 dark:text-blue-400">{totalActiveJobs} backfill job{totalActiveJobs !== 1 ? 's' : ''} running</span>
            <span className="text-blue-600 dark:text-blue-400 ml-2">{runningMonths.join(', ')}</span>
          </div>
          <span className="text-[10px] text-blue-400 ml-auto">Auto-refreshes every 15s</span>
        </div>
      )}

      {/* Current month day-level calendar */}
      {currentMonthDays.length > 0 && (
        <BackfillCalendar
          days={currentMonthDays}
          storeId={detail.storeId}
          platform={detail.platform}
          configId={configId}
          onRefresh={onRefresh}
        />
      )}

      {/* Color legend */}
      <div className="flex flex-wrap items-center gap-3 px-1">
        {[
          { color: 'bg-green-500', label: 'Completed' },
          { color: 'bg-blue-400', label: 'Running' },
          { color: 'bg-amber-400', label: 'Partial' },
          { color: 'bg-red-400', label: 'Errored' },
          { color: 'bg-gray-300', label: 'Empty' },
        ].map(({ color, label }) => (
          <span key={label} className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
            <span className={`w-2 h-2 rounded-full ${color}`} />
            {label}
          </span>
        ))}
      </div>

      {/* Past months grid */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Past Months</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1">
              {[3, 6, 12].map((n) => (
                <button key={n} onClick={() => onSelectPreset(n)} className="px-2 py-0.5 text-[10px] border border-gray-200 dark:border-gray-800 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-950">
                  Last {n}
                </button>
              ))}
              <button onClick={onSelectAll} className="px-2 py-0.5 text-[10px] border border-gray-200 dark:border-gray-800 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-950">
                All
              </button>
            </div>
            {selectedMonths.size > 0 && (
              <>
                <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{selectedMonths.size} selected</span>
                <button onClick={onDeselectAll} className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">Clear</button>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={forceBackfill}
                    onChange={(e) => onToggleForce(e.target.checked)}
                    className="w-3 h-3 rounded border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:ring-gray-900"
                  />
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">Force</span>
                </label>
                <button
                  onClick={onBackfillSelected}
                  disabled={acting === 'backfill-selected'}
                  className="px-2.5 py-1 text-xs font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1"
                >
                  {acting === 'backfill-selected' && <Loader2 className="w-3 h-3 animate-spin" />}
                  Backfill {selectedMonths.size}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
          {monthGrid.slice(1).map((cell) => {
            const isSelected = selectedMonths.has(cell.month);
            const colorMap: Record<string, string> = {
              completed: 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-900/50 text-green-700 dark:text-green-400',
              errored: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400',
              running: 'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-900/50 text-blue-700 dark:text-blue-400',
              partial: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900/50 text-amber-700 dark:text-amber-300',
              empty: 'bg-gray-50 dark:bg-gray-950 border-gray-200 dark:border-gray-800 text-gray-400 dark:text-gray-500',
            };
            const dotMap: Record<string, string> = {
              completed: 'bg-green-400',
              errored: 'bg-red-400',
              running: 'bg-blue-400 animate-pulse',
              partial: 'bg-amber-400',
              empty: 'bg-gray-300',
            };
            return (
              <button
                key={cell.month}
                onClick={() => onToggleMonth(cell.month)}
                className={`p-1.5 rounded-lg border text-left transition-all ${colorMap[cell.status] ?? ''}
                  ${isSelected ? 'ring-2 ring-gray-900 ring-offset-1' : 'hover:shadow-sm'}`}
                title={cell.errorMessage ? `Error: ${cell.errorMessage}` : `${cell.rows.toLocaleString()} rows`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-medium">{formatMonthLabel(cell.month)}</span>
                  <div className="flex items-center gap-1">
                    {(cell.activeJobs ?? 0) > 0 && (
                      <span className="text-[8px] px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 font-medium animate-pulse">
                        {cell.activeJobs} job{cell.activeJobs! > 1 ? 's' : ''}
                      </span>
                    )}
                    <span className={`w-1.5 h-1.5 rounded-full ${dotMap[cell.status] ?? ''}`} />
                  </div>
                </div>
                <div className="text-[9px] opacity-70 mt-0.5">
                  {cell.status === 'empty' && (cell.activeJobs ?? 0) === 0 ? 'No data' : formatRows(cell.rows)}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Runs Tab ─────────────────────────────────────

function RunsTab({
  runs,
  storeId,
  configId,
  acting,
  navigate,
  onRetryRun,
}: {
  runs: ConfigDetail['recentRuns'];
  storeId: number;
  configId: string;
  acting: string | null;
  navigate: ReturnType<typeof useNavigate>;
  onRetryRun: (run: IngestionRun) => void;
}) {
  const [runTab, setRunTab] = useState<string>('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    let list = runTab === 'all' ? runs : runs.filter((r) => r.triggerSource === runTab);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        (r.errorMessage?.toLowerCase().includes(q)) ||
        (r.platform?.toLowerCase().includes(q)) ||
        (r.runType?.toLowerCase().includes(q)) ||
        (r.windowStart?.includes(q))
      );
    }
    return list;
  }, [runs, runTab, search]);

  const storeActiveCount = useMemo(
    () => runs.filter((r) => r.triggerSource === 'store-active').length,
    [runs],
  );

  const successRate = filtered.length > 0
    ? Math.round((filtered.filter((r) => r.status === 'completed').length / filtered.length) * 100)
    : 0;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
        <ScrollText className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Recent Runs</span>
        {storeActiveCount > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 font-medium">
            {storeActiveCount} store-active
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className={`text-[10px] font-medium ${successRate >= 80 ? 'text-green-600' : successRate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
            {successRate}% success
          </span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500">{filtered.length} runs</span>
        </div>
      </div>

      {/* Search bar */}
      <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search runs by error, date, type..."
          className="w-full px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 placeholder:text-gray-300"
        />
      </div>

      {/* Filter tabs */}
      <div className="flex gap-0.5 px-2 pt-1.5 pb-0 border-b border-gray-100 dark:border-gray-800 overflow-x-auto">
        {RUN_TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setRunTab(t.value)}
            className={`px-2.5 py-1.5 text-[10px] font-medium border-b-2 transition-colors whitespace-nowrap ${
              runTab === t.value
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-400 dark:text-gray-500">
          {search ? `No runs matching "${search}"` : runTab === 'all' ? 'No runs yet' : `No ${runTab} runs`}
        </div>
      ) : (
        <div className="divide-y divide-gray-100 max-h-[600px] overflow-auto">
          {filtered.map((run) => {
            const rs = STATUS_CONFIG[run.status] ?? STATUS_CONFIG['completed']!;
            const RunIcon = rs.icon;
            const triggerLabel = TRIGGER_SOURCE_LABELS[run.triggerSource];
            const duration =
              run.startedAt && run.completedAt
                ? formatDuration(new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime())
                : run.startedAt
                  ? formatDuration(Date.now() - new Date(run.startedAt).getTime())
                  : null;
            const isRetrying = acting === `retry-run-${run.id}`;

            return (
              <div
                key={run.id}
                className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-950 transition-colors group"
              >
                <div
                  className="flex items-center gap-2.5 min-w-0 flex-1"
                  onClick={() => navigate(`/ingestion/${storeId}/${configId}/runs/${run.id}`)}
                >
                  <RunIcon className={`w-3.5 h-3.5 shrink-0 ${rs.className.split(' ')[0]}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-xs text-gray-900 dark:text-gray-100">
                      {RUN_TYPE_LABELS[run.runType] ?? run.runType}
                      {triggerLabel && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${triggerLabel.className}`}>
                          {triggerLabel.label}
                        </span>
                      )}
                      {run.windowStart && (
                        <span className="text-gray-400 dark:text-gray-500 font-normal">{run.windowStart}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                      <span title={new Date(run.startedAt).toLocaleString()}>{timeAgo(run.startedAt)}</span>
                      {duration && <span>{duration}</span>}
                      {run.rowsIngested > 0 && <span>{run.rowsIngested.toLocaleString()} rows</span>}
                      {run.windowsTotal != null && run.windowsTotal > 0 && (
                        <span className="text-blue-500">{run.windowsCompleted ?? 0}/{run.windowsTotal} windows</span>
                      )}
                      {run.backfillGroupId && (
                        <span className="px-1 py-0.5 rounded bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 font-mono" title={run.backfillGroupId}>
                          group
                        </span>
                      )}
                    </div>
                    {run.errorMessage && (
                      <div className="text-[10px] text-red-500 mt-0.5 truncate max-w-xs">{run.errorMessage}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {run.status === 'errored' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onRetryRun(run); }}
                      disabled={isRetrying}
                      title="Retry"
                      className="p-1 rounded border border-gray-200 dark:border-gray-800 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950 opacity-0 group-hover:opacity-100 transition-all disabled:opacity-40"
                    >
                      {isRetrying ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    </button>
                  )}
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
